import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, ChevronRight, Sparkles, TrendingUp, BookOpen, LineChart, Trophy, Scale, GraduationCap, Rocket, PieChart, FileText, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for categories
const CATEGORY_ICONS = {
  'product_roundup': TrendingUp,
  'how_to_guide': BookOpen,
  'creator_spotlight': Sparkles,
  'industry_trends': LineChart,
  'success_stories': Trophy,
  'tool_comparison': Scale,
  'beginner_guide': GraduationCap,
  'advanced_tips': Rocket,
  'market_analysis': PieChart,
  'case_study': FileText,
  // Legacy mapping for hardcoded articles
  'Product Roundup': TrendingUp,
  'How-To Guide': BookOpen,
  'Creator Spotlight': Sparkles,
};

// Fallback hardcoded articles (shown if API fails or returns empty)
const FALLBACK_ARTICLES = [
  {
    article_id: 'top-ai-tools-2026',
    slug: 'top-ai-tools-2026',
    category: 'product_roundup',
    category_name: 'Product Roundup',
    title: 'Top 5 AI Tools Every Creator Needs in 2026',
    excerpt: 'The AI landscape is evolving rapidly. Here are the must-have tools that will define creator success in 2026 and beyond.',
    author: 'The AI Ledger Team',
    created_at: '2026-01-01T00:00:00Z',
    read_time: '5 min read',
    featured: true,
    image_url: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80',
    content: `## The AI Revolution is Here

As we enter 2026, artificial intelligence has transformed from a buzzword into an essential toolkit for creators. Whether you're building products, creating content, or scaling your business, these five categories of AI tools are no longer optional—they're essential.

### 1. AI Content Generators

The new generation of content AI goes beyond simple text. Tools now understand context, maintain brand voice across thousands of pieces, and can generate everything from marketing copy to technical documentation.

**What to look for:** Multi-modal capabilities, brand voice training, and seamless integration with your existing workflow.

### 2. AI-Powered Analytics

Understanding your audience has never been more critical. Modern AI analytics don't just show you what happened—they predict what will happen and recommend actions.

**Key features:** Predictive modeling, automated insights, and real-time optimization suggestions.

### 3. AI Design Assistants

From logo generation to full website designs, AI design tools have matured significantly. The best ones now understand design principles and can maintain consistency across your entire brand.

**Pro tip:** Look for tools that offer style transfer and can learn from your existing brand assets.

### 4. AI Customer Service

Chatbots have evolved into intelligent assistants that can handle complex queries, escalate appropriately, and even predict customer needs before they arise.

**Must-have:** Natural conversation flow, seamless human handoff, and learning capabilities.

### 5. AI Code Assistants

Even non-technical creators are building products with AI code assistants. These tools can generate, debug, and optimize code while you focus on your vision.

**Game-changer:** Look for assistants that understand your entire codebase and can suggest architectural improvements.

## The Bottom Line

The creators who thrive in 2026 won't be those who avoid AI—they'll be the ones who master it. Start with one tool, get comfortable, and gradually expand your AI toolkit.

*Ready to find your perfect AI tools? Browse our marketplace for creator-vetted solutions.*`
  },
  {
    article_id: 'pricing-ai-products-2026',
    slug: 'pricing-ai-products-2026',
    category: 'how_to_guide',
    category_name: 'How-To Guide',
    title: 'How to Price Your AI Product for Maximum Revenue in 2026',
    excerpt: 'Pricing AI products is tricky. Too high and you lose customers. Too low and you leave money on the table. Here\'s the definitive guide.',
    author: 'The AI Ledger Team',
    created_at: '2026-01-01T00:00:00Z',
    read_time: '7 min read',
    featured: true,
    image_url: 'https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=800&q=80',
    content: `## The Art and Science of AI Product Pricing

Pricing your AI product correctly can mean the difference between a thriving business and a struggling side project. Here's what we've learned from analyzing thousands of successful AI products on our marketplace.

### Understanding Value-Based Pricing

Forget cost-plus pricing. In the AI world, your price should reflect the value you deliver, not the hours you spent building.

**The Value Formula:**
- Time saved for the customer
- Revenue generated or costs reduced
- Competitive alternatives and their pricing
- Your unique differentiators

### The Three Pricing Tiers That Work

After analyzing our top-selling products, we've identified a pattern:

**Entry Tier ($27-$47)**
- Perfect for impulse purchases
- Single use case or limited features
- Great for building your customer base

**Professional Tier ($97-$197)**
- The sweet spot for most AI tools
- Full feature access
- Includes updates for a defined period

**Premium/Enterprise ($297+)**
- White-label or agency rights
- Priority support
- Extended or lifetime updates

### Psychological Pricing Tactics

1. **Anchor High:** Show a higher "value" before your actual price
2. **Odd Numbers:** $97 converts better than $100
3. **Bundle Strategically:** Combine products for perceived value
4. **Limited Time:** Scarcity drives action (but be genuine)

## Action Steps

1. Calculate the ROI your product delivers
2. Research 5 competitors' pricing
3. Set up 2-3 pricing tiers
4. Test with a small audience before full launch
5. Review and adjust quarterly

*Need help pricing your AI product? Our creator community shares insights daily in the theailedger.ai forums.*`
  },
  {
    article_id: 'creator-spotlight-template',
    slug: 'creator-spotlight-template',
    category: 'creator_spotlight',
    category_name: 'Creator Spotlight',
    title: 'From Side Project to $50K: How One Creator Built an AI Empire',
    excerpt: 'Meet the creator who turned a weekend project into a thriving AI business. Here\'s their journey and the lessons learned along the way.',
    author: 'The AI Ledger Team',
    created_at: '2026-01-01T00:00:00Z',
    read_time: '6 min read',
    featured: false,
    image_url: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80',
    content: `## The Beginning

Every successful creator has an origin story. This one starts like many others—with a problem that needed solving and a weekend with nothing better to do.

"I was frustrated with how long it took to create marketing content," our featured creator recalls. "So I built something to fix it for myself. I never imagined others would want it too."

### The First Sale

The moment everything changed wasn't dramatic. It was a simple notification: "You've made a sale."

"I remember staring at my phone, thinking it was a mistake. Someone I'd never met just paid money for something I built. That's when it hit me—this could be real."

### Key Lessons Learned

1. **Start before you're ready.** "My first version was embarrassing. But it worked, and that's what mattered."

2. **Listen obsessively to customers.** "Every feature request is a potential sale."

3. **Price for value, not time.** "I used to charge by the hour I spent. Now I charge by the problem I solve."

4. **Build in public.** "Sharing my journey brought customers, partners, and friends."

5. **Diversify your products.** "One product is risky. A product suite is a business."

### Advice for New Creators

"Don't wait for permission. Don't wait for perfect. Build something that helps one person, then figure out how to help more. That's all business is."

*Want to be featured? Successful creators on theailedger.ai are eligible for our Creator Spotlight series. Keep building!*`
  },
  {
    article_id: 'ai-trends-2026',
    slug: 'ai-trends-2026',
    category: 'industry_trends',
    category_name: 'Industry Trends',
    title: '7 AI Trends That Will Dominate 2026 (And How to Profit)',
    excerpt: 'Stay ahead of the curve. These emerging AI trends represent massive opportunities for creators who move fast.',
    author: 'The AI Ledger Team',
    created_at: '2026-01-01T00:00:00Z',
    read_time: '8 min read',
    featured: false,
    image_url: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=800&q=80',
    content: `## The AI Landscape is Shifting

2026 is shaping up to be a pivotal year for AI. The hype cycle is maturing into real, practical applications. Here's where the smart money is going.

### Trend 1: Hyper-Personalization

Generic AI is out. Personalized AI is in. Tools that adapt to individual users' styles, preferences, and workflows will dominate.

**Opportunity:** Build AI products that learn and customize per user.

### Trend 2: AI Agents

Single-task AI is giving way to AI agents that can handle complex, multi-step workflows autonomously.

**Opportunity:** Create agent-based tools that handle entire processes, not just individual tasks.

### Trend 3: Voice-First Interfaces

Typing is becoming optional. Voice interaction with AI is becoming natural and expected.

**Opportunity:** Add voice capabilities to existing tools or build voice-native products.

### Trend 4: AI for Small Business

Enterprise AI is trickling down. Small businesses want AI power at small business prices.

**Opportunity:** Package enterprise-grade AI capabilities for solopreneurs and small teams.

### Trend 5: Ethical AI Products

Consumers increasingly care about how AI is built. Transparency and ethics are becoming selling points.

**Opportunity:** Build with ethics in mind and make it a feature, not an afterthought.

### Trend 6: AI-Powered Education

Learning is being transformed. Personalized tutors, adaptive curricula, and skill assessment AI are exploding.

**Opportunity:** Create AI educational tools for any niche you understand deeply.

### Trend 7: AI Integration Tools

The market is fragmenting. Tools that connect and orchestrate multiple AI services will be essential.

**Opportunity:** Build bridges between popular AI tools and platforms.

## The Bottom Line

The AI gold rush isn't over—it's just getting started. But the winners in 2026 won't be those building generic tools. They'll be the creators who go deep on specific problems for specific audiences.

*Which trend will you pursue? Start exploring solutions on theailedger.ai today.*`
  },
  {
    article_id: 'first-ai-product-guide',
    slug: 'first-ai-product-guide',
    category: 'beginner_guide',
    category_name: 'Beginner Guide',
    title: 'Launch Your First AI Product in 30 Days: A Step-by-Step Guide',
    excerpt: 'No coding required. No massive budget needed. Here\'s exactly how to go from idea to launched AI product in just one month.',
    author: 'The AI Ledger Team',
    created_at: '2026-01-01T00:00:00Z',
    read_time: '10 min read',
    featured: false,
    image_url: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80',
    content: `## Your 30-Day Launch Plan

Think you need to be a developer to sell AI products? Think again. This guide will take you from zero to launched in 30 days.

### Week 1: Discovery & Validation (Days 1-7)

**Day 1-2: Find Your Niche**
- What problems do you understand deeply?
- What questions do people constantly ask you?
- Where do existing solutions fall short?

**Day 3-4: Validate Demand**
- Search for people complaining about this problem
- Check if competitors exist (competitors = demand)
- Ask your network if they'd pay for a solution

**Day 5-7: Define Your MVP**
- What's the simplest version that solves the core problem?
- Write down exactly what it will do (and won't do)
- Sketch the user flow

### Week 2: Building (Days 8-14)

**Day 8-9: Choose Your Tools**
No-code options:
- Zapier + ChatGPT for automation
- Bubble for web apps
- Notion + AI for databases

Low-code options:
- Streamlit for Python apps
- Retool for internal tools
- Webflow + APIs for websites

**Day 10-12: Build Your MVP**
- Focus on the core feature only
- Don't worry about design—function first
- Test constantly as you build

**Day 13-14: Polish & Test**
- Fix obvious bugs
- Add basic styling
- Have 3-5 people test it

### Week 3: Preparation (Days 15-21)

**Day 15-16: Create Your Listing**
- Write compelling product description
- Highlight benefits, not features
- Create screenshots/demo video

**Day 17-18: Set Your Price**
- Research competitor pricing
- Start with a launch discount
- Plan your pricing tiers

**Day 19-21: Prepare Marketing**
- Write launch announcement
- Create social media content
- Prepare email to your network

### Week 4: Launch (Days 22-30)

**Day 22: Soft Launch**
- List on theailedger.ai
- Share with close network
- Gather initial feedback

**Day 23-25: Iterate**
- Fix issues from feedback
- Improve based on user behavior
- Enhance your listing

**Day 26-28: Public Launch**
- Announce on social media
- Share in relevant communities
- Reach out to potential reviewers

**Day 29-30: Analyze & Plan**
- Review what worked
- Plan improvements
- Set goals for month 2

## You've Got This

Every successful AI creator started exactly where you are now. The difference between those who succeed and those who don't? Taking action.

Your 30 days start now.

*Ready to launch? Create your seller account on theailedger.ai and join thousands of AI creators building the future.*`
  }
];

// Default categories
const DEFAULT_CATEGORIES = [
  { id: 'all', name: 'All Articles', icon: 'BookOpen' },
  { id: 'product_roundup', name: 'Product Roundups', icon: 'TrendingUp' },
  { id: 'how_to_guide', name: 'How-To Guides', icon: 'BookOpen' },
  { id: 'creator_spotlight', name: 'Creator Spotlights', icon: 'Sparkles' },
  { id: 'industry_trends', name: 'Industry Trends', icon: 'LineChart' },
  { id: 'success_stories', name: 'Success Stories', icon: 'Trophy' },
  { id: 'beginner_guide', name: 'Beginner Guides', icon: 'GraduationCap' },
  { id: 'advanced_tips', name: 'Advanced Tips', icon: 'Rocket' },
];

function Resources() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch articles from API
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/api/articles`);
        if (response.data && response.data.length > 0) {
          setArticles(response.data);
          // Auto-open article from URL query param
          const articleSlug = searchParams.get('article');
          if (articleSlug) {
            const match = response.data.find(a => a.slug === articleSlug || a.article_id === articleSlug);
            if (match) setSelectedArticle(match);
          }
        } else {
          setArticles(FALLBACK_ARTICLES);
        }
      } catch (err) {
        console.error('Failed to fetch articles:', err);
        setArticles(FALLBACK_ARTICLES);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [searchParams]);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/api/articles/meta/categories`);
        if (response.data && response.data.length > 0) {
          const allCategory = { id: 'all', name: 'All Articles', icon: 'BookOpen' };
          setCategories([allCategory, ...response.data]);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };

    fetchCategories();
  }, []);

  const filteredArticles = selectedCategory === 'all' 
    ? articles 
    : articles.filter(a => a.category === selectedCategory || a.category_name === selectedCategory);

  const featuredArticles = articles.filter(a => a.featured);

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'January 2026';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } catch {
      return 'January 2026';
    }
  };

  // Get icon component for category
  const getCategoryIcon = (category) => {
    return CATEGORY_ICONS[category] || BookOpen;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  if (selectedArticle) {
    const CategoryIcon = getCategoryIcon(selectedArticle.category);
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        {/* Article View */}
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <Button
            variant="ghost"
            onClick={() => setSelectedArticle(null)}
            className="mb-6 text-neutral-400 hover:text-white"
            data-testid="back-to-resources-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Resources
          </Button>

          {/* Article Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#00F0FF]/20 text-[#00F0FF]">
                {selectedArticle.category_name || selectedArticle.category}
              </span>
              {selectedArticle.ai_generated && (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#00F0FF]/20 text-[#00F0FF]">
                  AI Generated
                </span>
              )}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {selectedArticle.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-400">
              <span className="flex items-center gap-1">
                <User className="w-4 h-4" />
                {selectedArticle.author}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(selectedArticle.published_at || selectedArticle.created_at)}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {selectedArticle.read_time}
              </span>
            </div>
          </div>

          {/* Featured Image */}
          <div className="rounded-2xl overflow-hidden mb-8">
            <img 
              src={selectedArticle.image_url || selectedArticle.image} 
              alt={selectedArticle.title}
              className="w-full h-64 md:h-96 object-cover"
            />
          </div>

          {/* Article Content */}
          <article className="prose prose-invert prose-lg max-w-none">
            <div 
              className="text-neutral-300 leading-relaxed"
              style={{ whiteSpace: 'pre-line' }}
            >
              {selectedArticle.content.split('\n').map((line, i) => {
                const lineKey = `line-${i}-${line.slice(0, 20).replace(/\W/g, '')}`;
                if (line.startsWith('## ')) {
                  return <h2 key={lineKey} className="text-2xl font-bold text-white mt-8 mb-4">{line.replace('## ', '')}</h2>;
                } else if (line.startsWith('### ')) {
                  return <h3 key={lineKey} className="text-xl font-bold text-white mt-6 mb-3">{line.replace('### ', '')}</h3>;
                } else if (line.startsWith('**') && line.endsWith('**')) {
                  return <p key={lineKey} className="font-bold text-white my-2">{line.replace(/\*\*/g, '')}</p>;
                } else if (line.startsWith('- ') || line.startsWith('❌ ') || line.startsWith('✅ ')) {
                  return <p key={lineKey} className="ml-4 my-1">{line}</p>;
                } else if (line.startsWith('*') && line.endsWith('*')) {
                  return <p key={lineKey} className="italic text-neutral-400 my-4">{line.replace(/\*/g, '')}</p>;
                } else if (line.trim()) {
                  return <p key={lineKey} className="my-4">{line}</p>;
                }
                return null;
              })}
            </div>
          </article>

          {/* CTA */}
          <div className="mt-12 p-8 rounded-2xl bg-gradient-to-r from-white/20 to-[#00F0FF]/20 border border-white/30 text-center">
            <h3 className="text-xl font-bold mb-2">Ready to Start Your AI Journey?</h3>
            <p className="text-neutral-400 mb-4">Join thousands of creators on theailedger.ai</p>
            <Button
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-[#00F0FF] to-white hover:opacity-90 text-white font-bold px-8"
              data-testid="explore-marketplace-btn"
            >
              Explore the Marketplace
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="resources-page">
      {/* Header */}
      <div className="container mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6 text-neutral-400 hover:text-white"
          data-testid="back-to-marketplace-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Marketplace
        </Button>

        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Resources for <span className="text-white">AI Creators</span>
          </h1>
          <p className="text-neutral-400 text-lg max-w-2xl mx-auto">
            Guides, insights, and inspiration to help you build and sell successful AI products
          </p>
        </div>

        {/* Featured Articles */}
        {featuredArticles.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#00F0FF]" />
              Featured
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {featuredArticles.slice(0, 2).map((article) => {
                const CategoryIcon = getCategoryIcon(article.category);
                return (
                  <div
                    key={article.article_id || article.slug}
                    onClick={() => setSelectedArticle(article)}
                    className="group cursor-pointer rounded-2xl overflow-hidden bg-neutral-900/50 border border-white/10 hover:border-white/50 transition-all"
                    data-testid={`featured-article-${article.slug}`}
                  >
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={article.image_url || article.image} 
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#00F0FF]/20 text-[#00F0FF]">
                          {article.category_name || article.category}
                        </span>
                        <span className="text-xs text-neutral-500">{article.read_time}</span>
                      </div>
                      <h3 className="text-xl font-bold mb-2 group-hover:text-white transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-neutral-400 text-sm line-clamp-2">{article.excerpt}</p>
                      <div className="mt-4 flex items-center text-[#00F0FF] text-sm font-medium">
                        Read Article <ChevronRight className="w-4 h-4 ml-1" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => {
            const IconComponent = CATEGORY_ICONS[cat.id] || CATEGORY_ICONS[cat.icon] || BookOpen;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                  selectedCategory === cat.id
                    ? 'bg-[#00F0FF] text-black'
                    : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700'
                }`}
                data-testid={`category-filter-${cat.id}`}
              >
                <IconComponent className="w-4 h-4" />
                {cat.name}
              </button>
            );
          })}
        </div>

        {/* All Articles Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredArticles.map((article) => {
            const CategoryIcon = getCategoryIcon(article.category);
            return (
              <div
                key={article.article_id || article.slug}
                onClick={() => setSelectedArticle(article)}
                className="group cursor-pointer rounded-2xl overflow-hidden bg-neutral-900/50 border border-white/10 hover:border-white/50 transition-all"
                data-testid={`article-card-${article.slug}`}
              >
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={article.image_url || article.image} 
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <CategoryIcon className="w-4 h-4 text-white" />
                    <span className="text-xs text-neutral-500">{article.category_name || article.category}</span>
                    <span className="text-xs text-neutral-600">•</span>
                    <span className="text-xs text-neutral-500">{article.read_time}</span>
                  </div>
                  <h3 className="text-lg font-bold mb-2 group-hover:text-white transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-neutral-400 text-sm line-clamp-2">{article.excerpt}</p>
                  <div className="mt-4 text-neutral-500 text-xs">
                    {formatDate(article.published_at || article.created_at)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredArticles.length === 0 && (
          <div className="text-center py-12">
            <p className="text-neutral-400">No articles found in this category yet.</p>
          </div>
        )}

        {/* Newsletter CTA */}
        <div className="mt-16 p-8 md:p-12 rounded-3xl bg-gradient-to-r from-white/10 to-[#00F0FF]/10 border border-white/10 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">Stay Ahead of the AI Curve</h2>
          <p className="text-neutral-400 mb-6 max-w-xl mx-auto">
            Get the latest guides, creator spotlights, and AI product insights delivered to your inbox.
          </p>
          <Button
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-[#00F0FF] to-white hover:opacity-90 text-white font-bold px-8 py-6"
            data-testid="join-newsletter-btn"
          >
            Join theailedger.ai
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/10 mt-16 py-8">
        <div className="container mx-auto px-4 text-center text-neutral-500 text-sm">
          © 2026 theailedger.ai. All rights reserved.
        </div>
      </footer>
    </div>
  );
}

export default Resources;
